import React, { useMemo, useState, useEffect} from "react";
import { DataGrid } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import ColumnManagerDrawer from "./ColumnManagerDrawer";
import axios from "axios";
import Stack from "@mui/material/Stack";
import * as XLSX from "xlsx";

function cellWithTooltip(value, source) {
  return (
    <Tooltip title={source || "No source"} placement="top" enterDelay={200} arrow>
      <span style={{ cursor: source ? "help" : "default" }}>
        {value === null || value === undefined ? "" : String(value)}
      </span>
    </Tooltip>
  );
}

function formatLabValue(lab) {
  if (!lab) return null;
  const v = lab.value ?? null;
  const u = lab.unit ?? null;
  if (v === null || v === undefined || v === "") return null;
  return u ? `${v} ${u}` : String(v);
}

function getFieldSource(row, field) {
  // base 欄位 source
  if (row?.__src && row.__src[field]) return row.__src[field];
  // lab 欄位 source
  if (row?.__labSrc && row.__labSrc[field]) return row.__labSrc[field];
  return null;
}

function getMergedSource(row, canonField, mergeMap) {
  const fromFields = (mergeMap && mergeMap[canonField]) ? mergeMap[canonField] : [canonField];
  const seen = new Set();
  const srcs = [];
  for (const f of fromFields) {
    const s = getFieldSource(row, f);
    if (s && !seen.has(s)) {
      seen.add(s);
      srcs.push(s);
    }
  }
  return srcs.length ? srcs.join("\n\n") : null;
}

function mergeColumnsAndRowsKeepSources(columns, rows) {
  // 以 headerName 作 canonical，記錄來源欄位 (field) list
  const mergeMap = {}; // canon -> [origField...]
  for (const c of columns) {
    const canon = c.headerName ?? c.field;
    if (!mergeMap[canon]) mergeMap[canon] = [];
    mergeMap[canon].push(c.field);
  }

  // columns：canon 當 field/headerName（避免重複）
  const newColumns = Object.keys(mergeMap).map((canon) => {
    // 找第一個原欄位當 base 設定（寬度、editable 等）
    const firstField = mergeMap[canon][0];
    const base = columns.find((c) => c.field === firstField) || { width: 160 };

    return {
      ...base,
      field: canon,
      headerName: canon,
      // tooltip 用 mergeMap 找來源
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, canon, mergeMap)),
    };
  });

  const newRows = rows.map((r, idx) => {
    const out = {
      id: r.id ?? idx,
      doc_id: r.doc_id,
      __src: r.__src || {},
      __labSrc: r.__labSrc || {},
    };

    for (const canon of Object.keys(mergeMap)) {
      if (canon === "doc_id") continue;
      let v = null;
      for (const f of mergeMap[canon]) {
        const cand = r[f];
        if (cand !== null && cand !== undefined && cand !== "") {
          v = cand;
          break;
        }
      }
      out[canon] = v;
    }

    return out;
  });

  return { columns: newColumns, rows: newRows, mergeMap };
}

function toCsvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
}

function downloadCsvFront(columns, rows, filename = "case_table.csv") {
  const fields = columns.map((c) => c.field);
  const header = fields.map((f) => toCsvCell(f)).join(",");
  const body = rows
    .map((r) => fields.map((f) => toCsvCell(r[f])).join(","))
    .join("\n");

  const csv = header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadXlsxFront(columns, rows, filename = "case_table.xlsx") {
  const fields = columns.map((c) => c.field);

  // 用 headerName 當表頭（如果有）
  const header = columns.map((c) => c.headerName ?? c.field);

  // AO A (array-of-arrays)：第一列表頭 + 後面每列資料
  const aoa = [
    header,
    ...rows.map((r) => fields.map((f) => (r[f] === undefined ? null : r[f]))),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // 可選：簡單設定欄寬（依字串長度估）
  ws["!cols"] = header.map((h, i) => {
    let maxLen = String(h ?? "").length;
    for (const r of rows) {
      const v = r[fields[i]];
      const len = v == null ? 0 : String(v).length;
      if (len > maxLen) maxLen = len;
    }
    return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Cases");
  XLSX.writeFile(wb, filename);
}

export default function CaseTable({ records = [], onEditCell, onModelReady}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerMap, setHeaderMap] = useState({});
  const [mergedModel, setMergedModel] = React.useState(null);
  const [rowState, setRowState] = useState([]);
  const safeRecords = useMemo(() => {
    if (Array.isArray(records)) return records;
    if (Array.isArray(records?.results)) return records.results;
    if (records && typeof records === "object") return Object.values(records); // 後端若回 dict
    return [];
  }, [records]);

  // 蒐集所有 labs 名稱（name_raw）當作動態欄位
  const labKeys = useMemo(() => {
    const s = new Set();
    for (const r of safeRecords) {
      const labs = r?.labs;
      if (!Array.isArray(labs)) continue;
      for (const lab of labs) {
        const name = lab?.name_raw;
        if (name) s.add(name);
      }
    }
    return Array.from(s);
  }, [safeRecords]);

  function stripMeta(row) {
    const { __src, __labSrc, id, ...rest } = row;
    return rest;
    }

  async function downloadBlob(url, payload, filename) {
    const res = await axios.post(url, payload, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    }

  // 固定欄位 columns
  const baseColumns = useMemo(() => ([
    { field: "doc_id", headerName: "doc_id", width: 180 },

    { field: "age", headerName: "Age", width: 90, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "age", null))
    },

    { field: "sex", headerName: "Sex", width: 110, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "sex", null))},

    { field: "follow_up_time", headerName: "Follow-up", width: 140, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "follow_up_time", null))},

    { field: "event", headerName: "Event", width: 90, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "event", null))},

    { field: "event_type", headerName: "Event type", width: 160, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "event_type", null))},

    { field: "diagnosis_main", headerName: "Diagnosis", width: 260, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "diagnosis_main", null)) },

    { field: "tumor_size_cm", headerName: "Tumor size (cm)", width: 160, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "tumor_size_cm", null)) },

    { field: "first_visit", headerName: "First visit", width: 160, editable: true,
      renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, "first_visit", null)) },
  ]), []);

  // labs columns：每個 name_raw 一欄（會很多）
  const labColumns = useMemo(() => {
    return labKeys.map((name) => {
      const field = `lab__${name}`;
      return {
        field,
        headerName: name,
        width: 220,
        editable: true, // 先允許改值
        renderCell: (p) => cellWithTooltip(p.value, getMergedSource(p.row, field, null)),
      };
    });
  }, [labKeys]);

  const columns = useMemo(() => {
  const merged = [...baseColumns, ...labColumns];
    return merged.map((c) => ({
        ...c,
        headerName: headerMap[c.field] ?? c.headerName,
    }));
    }, [baseColumns, labColumns, headerMap]);

  // rows：把 labs pivot 到 row 裡
  const rows = useMemo(() => {
    return safeRecords.map((r) => {
      const row = {
        id: r.doc_id,
        doc_id: r.doc_id,

        age: r.age?.value ?? null,
        sex: r.sex?.value ?? null,
        follow_up_time: r.follow_up_time?.value ?? null,
        event: r.event?.value ?? null,
        event_type: r.event_type?.value ?? null,
        diagnosis_main: r.diagnosis_main?.value ?? null,
        tumor_size_cm: r.tumor_size_cm?.value ?? null,
        first_visit: r.clinical_dates?.first_visit?.value ?? null,

        __src: {
          age: r.age?.source ?? null,
          sex: r.sex?.source ?? null,
          follow_up_time: r.follow_up_time?.source ?? null,
          event: r.event?.source ?? null,
          event_type: r.event_type?.source ?? null,
          diagnosis_main: r.diagnosis_main?.source ?? null,
          tumor_size_cm: r.tumor_size_cm?.source ?? null,
          first_visit: r.clinical_dates?.first_visit?.source ?? null,
        },

        // labs source tooltip
        __labSrc: {},
      };

      // pivot labs
      const labs = Array.isArray(r?.labs) ? r.labs : [];
      for (const lab of labs) {
        const name = lab?.name_raw;
        if (!name) continue;
        const field = `lab__${name}`;
        row[field] = formatLabValue(lab);
        row.__labSrc[field] = lab?.source ?? null;
      }

      return row;
    });
  }, [safeRecords, labKeys]);

  useEffect(() => {
    setRowState((prev) => {
        // 第一次載入
        if (!prev.length) return rows;

        // 用 id 合併
        const byId = new Map(prev.map((r) => [r.id, r]));
        const merged = rows.map((r) => byId.get(r.id) ?? r);

        // 如果 prev 裡有 rows 沒有的
        // for (const [id, r] of byId) if (!merged.find(x => x.id === id)) merged.push(r);

        return merged;
    });
    }, [rows]);

  const processRowUpdate = async (newRow, oldRow) => {
    setRowState((prev) =>
        prev.map((r) => (r.id === newRow.id ? newRow : r))
    );

    const changed = {};
    for (const k of Object.keys(newRow)) {
        if (k === "__src" || k === "__labSrc" || k === "id") continue;
        if (newRow[k] !== oldRow[k]) changed[k] = newRow[k];
    }

    if (Object.keys(changed).length && onEditCell) {
        await onEditCell(newRow.doc_id, changed);
    }
    return newRow;
    };

  const gridColumns = mergedModel?.columns ?? columns;
  const gridRows = mergedModel?.rows ?? rowState;

  useEffect(() => {
    if (!gridColumns?.length || !gridRows?.length) return;

    onModelReady?.({
        columns: gridColumns.map(c => ({ field: c.field, headerName: c.headerName })),
        rows: gridRows.map(stripMeta),
    });
    }, [gridColumns, gridRows, onModelReady]);

  return (
    <div style={{ height: 680, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Button variant="outlined" onClick={() => setDrawerOpen(true)}>
            Column Settings
        </Button>
        </div>

        <ColumnManagerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        columns={columns}
        headerMap={headerMap}
        onApplyHeaderMap={(m) => setHeaderMap(m)}
        />
      <DataGrid
        rows={gridRows}
        columns={gridColumns}
        disableRowSelectionOnClick
        processRowUpdate={processRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
            variant="outlined"
            onClick={async () => {
            // 前端合併：保留 __src/__labSrc + mergeMap
            const merged = mergeColumnsAndRowsKeepSources(gridColumns, rowState);

            setMergedModel({
                columns: merged.columns,
                rows: merged.rows,
                mergeMap: merged.mergeMap,
            });

            // 仍可存 snapshot（建議存「值」+ mergeMap，分析直接吃）
            await axios.post("/api/save_table_snapshot", {
                columns: merged.columns.map((c) => ({ field: c.field, headerName: c.headerName })),
                rows: merged.rows.map(stripMeta), // 存值就好
                mergeMap: merged.mergeMap,
            });
            }}
        >
            Re-render / Merge columns
        </Button>

        <Button
            variant="outlined"
            onClick={async () => {
            const curCols = mergedModel?.columns ?? gridColumns;
            const curRows = (mergedModel?.rows ?? gridRows).map(stripMeta);

            downloadCsvFront(curCols, curRows, "case_table.csv");

            await axios.post("/api/save_table_snapshot", {
                columns: curCols.map((c) => ({ field: c.field, headerName: c.headerName })),
                rows: curRows,
                mergeMap: mergedModel?.mergeMap ?? null,
            });
            }}
        >
            Download CSV
        </Button>

        <Button
            variant="contained"
            onClick={async () => {
                const curCols = mergedModel?.columns ?? gridColumns;
                const curRows = (mergedModel?.rows ?? gridRows).map(stripMeta);

                downloadXlsxFront(curCols, curRows, "case_table.xlsx");

                await axios.post("/api/save_table_snapshot", {
                columns: curCols.map((c) => ({ field: c.field, headerName: c.headerName })),
                rows: curRows,
                mergeMap: mergedModel?.mergeMap ?? null,
                });
            }}
            >
            Download Excel
            </Button>
        </Stack>
    </div>
  );
}
